import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import type { Contact } from '@otw/models/leads/contacts'
import type { Terminal } from '@otw/models/leads/terminals'
import { getTerminalDisplayName } from '@otw/models/leads/terminals'
import type { PickupLocation } from '@otw/models'

export interface TerminalCompanyHierarchyEntry {
  company: ContactCompany
  contacts: Contact[]
}

/** Companies that list this terminal in `terminalIds`. */
export function getTerminalCompanies(
  terminalId: string,
  companies: ContactCompany[],
): ContactCompany[] {
  const id = terminalId.trim()
  if (!id) return []
  return companies
    .filter((company) => (company.terminalIds ?? []).includes(id))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Terminals associated with a company. */
export function getCompanyTerminals(
  company: Pick<ContactCompany, 'terminalIds'>,
  terminals: Terminal[],
): Terminal[] {
  const ids = new Set(company.terminalIds ?? [])
  if (ids.size === 0) return []
  return terminals
    .filter((terminal) => ids.has(terminal.id))
    .sort((a, b) =>
      getTerminalDisplayName(a).localeCompare(getTerminalDisplayName(b)),
    )
}

export interface ContactTerminalLink {
  terminal: Terminal
  companies: ContactCompany[]
}

/**
 * Terminals associated with a contact, derived transitively through the
 * contact's company memberships. Associations are stored on companies.
 */
export function getContactTerminals(
  contactId: string,
  companies: ContactCompany[],
  terminals: Terminal[],
): ContactTerminalLink[] {
  const trimmedId = contactId.trim()
  if (!trimmedId) return []

  const memberCompanies = companies.filter((company) =>
    company.members.some((m) => m.contactId === trimmedId),
  )
  if (memberCompanies.length === 0) return []

  const byTerminalId = new Map<string, ContactCompany[]>()
  for (const company of memberCompanies) {
    for (const terminalId of company.terminalIds ?? []) {
      const list = byTerminalId.get(terminalId) ?? []
      list.push(company)
      byTerminalId.set(terminalId, list)
    }
  }

  const terminalById = new Map(terminals.map((t) => [t.id, t]))
  const links: ContactTerminalLink[] = []
  for (const [terminalId, linkedCompanies] of byTerminalId) {
    const terminal = terminalById.get(terminalId)
    if (!terminal) continue
    links.push({
      terminal,
      companies: [...linkedCompanies].sort((a, b) => a.name.localeCompare(b.name)),
    })
  }

  return links.sort((a, b) =>
    getTerminalDisplayName(a.terminal).localeCompare(getTerminalDisplayName(b.terminal)),
  )
}

/** Company → nested contacts hierarchy for a terminal preview. */
export function buildTerminalCompanyHierarchy(
  terminalId: string,
  companies: ContactCompany[],
  contacts: Contact[],
): TerminalCompanyHierarchyEntry[] {
  const contactById = new Map(contacts.map((c) => [c.id, c]))
  return getTerminalCompanies(terminalId, companies).map((company) => {
    const companyContacts = company.members
      .map((m) => contactById.get(m.contactId))
      .filter((c): c is Contact => Boolean(c))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    return { company, contacts: companyContacts }
  })
}

/** De-duplicated union of company dropoff ids and associated terminal location ids. */
export function resolveCompanyDeliveryLocationIds(
  company: Pick<ContactCompany, 'dropoffLocationIds' | 'terminalIds'>,
  terminals: Terminal[],
): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  const push = (id: string | null | undefined) => {
    const trimmed = id?.trim()
    if (!trimmed || seen.has(trimmed)) return
    seen.add(trimmed)
    out.push(trimmed)
  }

  for (const id of company.dropoffLocationIds ?? []) push(id)

  const terminalIds = new Set(company.terminalIds ?? [])
  for (const terminal of terminals) {
    if (!terminalIds.has(terminal.id)) continue
    push(terminal.pickup_location_id)
  }

  return out
}

/** Map pickup_location_id → terminal for badging delivery options. */
export function buildTerminalByPickupLocationId(
  terminals: Terminal[],
): Map<string, Terminal> {
  const map = new Map<string, Terminal>()
  for (const terminal of terminals) {
    const id = terminal.pickup_location_id?.trim()
    if (!id || map.has(id)) continue
    map.set(id, terminal)
  }
  return map
}

export function formatTerminalAddress(
  terminal: Terminal,
  location: PickupLocation | undefined | null,
): string {
  if (!location) return ''
  const line1 = location.address?.trim() ?? ''
  const cityPart = [location.city, location.state]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(', ')
  const tail = [cityPart, location.zip?.trim()].filter(Boolean).join(' ')
  return [line1, tail].filter(Boolean).join(' • ')
}
